import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { projectId, publicAnonKey } from '../utils/supabase/info';

export type UserRole = 'administrator' | 'staff' | 'student' | 'encoder' | 'cashier';
export type RegistrationStatus = 'approved' | 'pending_verification' | 'rejected';

export interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  studentId?: string; // Internal ID used for queue matching - displayed as "ID Number"
  windowNumber?: number;
  photo?: string;
  contactNumber?: string;
  // Extended student profile fields
  sex?: string;
  birthday?: string;
  birthplace?: string;
  permanentAddress?: string;
  course?: string;
  isGraduate?: boolean;
  academicYear?: string;
  lastEnrollment?: string;
  // Clearance status fields
  clearanceStatus?: 'not_submitted' | 'verified' | 'incomplete';
  clearanceRemarks?: string;
  clearanceVerifiedAt?: string;
  createdAt?: string;
  registrationStatus?: RegistrationStatus;
  registrationSource?: string;
  registrationSubmittedAt?: string;
  registrationReviewedAt?: string;
  registrationReviewedBy?: string;
}

export interface AuthActionResult {
  success: boolean;
  message?: string;
  requiresApproval?: boolean;
}

interface AuthContextType {
  user: User | null;
  accessToken: string | null;
  login: (email: string, password: string) => Promise<AuthActionResult>;
  signup: (email: string, password: string, name: string, role: UserRole, additionalInfo?: any) => Promise<AuthActionResult>;
  logout: () => void;
  updateProfile: (updates: Partial<User>) => Promise<void>;
  loading: boolean;
  refreshSession: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_BASE = `https://${projectId}.supabase.co/functions/v1/make-server-d4ddf86c`;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing session in localStorage
    const storedToken = localStorage.getItem('access_token');
    const storedUser = localStorage.getItem('user');
    
    if (storedToken && storedUser) {
      setAccessToken(storedToken);
      setUser(JSON.parse(storedUser));
      // Validate the stored token is still usable
      validateToken(storedToken).then(valid => {
        if (!valid) {
          console.warn('Stored access token is no longer valid, attempting refresh...');
          refreshSession();
        }
      });
    }
    setLoading(false);
  }, []);

  // Validate token by calling the /auth/me endpoint
  const validateToken = async (token: string): Promise<boolean> => {
    try {
      const response = await fetch(`${API_BASE}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      return response.ok;
    } catch {
      return false;
    }
  };

  // Refresh session using stored refresh_token
  const refreshSession = async (): Promise<string | null> => {
    const storedRefreshToken = localStorage.getItem('refresh_token');
    if (!storedRefreshToken) {
      console.warn('No refresh token available. Session expired.');
      setAccessToken(null);
      localStorage.removeItem('access_token');
      return null;
    }

    try {
      const response = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${publicAnonKey}`
        },
        body: JSON.stringify({ refresh_token: storedRefreshToken })
      });

      if (response.ok) {
        const data = await response.json();
        setAccessToken(data.access_token);
        localStorage.setItem('access_token', data.access_token);
        if (data.refresh_token) {
          localStorage.setItem('refresh_token', data.refresh_token);
        }
        console.log('Session refreshed successfully');
        return data.access_token;
      } else {
        console.warn('Token refresh failed. Clearing session.');
        setAccessToken(null);
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        return null;
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      setAccessToken(null);
      localStorage.removeItem('access_token');
      return null;
    }
  };

  const login = async (email: string, password: string): Promise<AuthActionResult> => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout

      const response = await fetch(`${API_BASE}/auth/signin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${publicAnonKey}`
        },
        body: JSON.stringify({ email, password }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Login error:', errorData.error || response.statusText);
        return {
          success: false,
          message: errorData.error || 'Login failed',
        };
      }

      const data = await response.json();
      
      if (data.user) {
        setUser(data.user);
        setAccessToken(data.access_token);
        localStorage.setItem('access_token', data.access_token);
        if (data.refresh_token) {
          localStorage.setItem('refresh_token', data.refresh_token);
        }
        localStorage.setItem('user', JSON.stringify(data.user));
        return { success: true };
      }
      
      return { success: false, message: 'Login failed' };
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          console.error('Login timeout - server may be unavailable');
          return { success: false, message: 'Login timed out. Please try again.' };
        } else {
          console.error('Login error:', error.message);
          return { success: false, message: error.message };
        }
      }
      return { success: false, message: 'Login failed' };
    }
  };

  const signup = async (
    email: string, 
    password: string, 
    name: string, 
    role: UserRole,
    additionalInfo?: any
  ): Promise<AuthActionResult> => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout

      const response = await fetch(`${API_BASE}/auth/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${publicAnonKey}`
        },
        body: JSON.stringify({
          email,
          password,
          name,
          role,
          ...(role === 'student' && { studentId: additionalInfo?.studentId })
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Signup error:', errorData.error || response.statusText);
        return {
          success: false,
          message: errorData.error || 'Signup failed',
        };
      }

      const data = await response.json();
      
      if (data.user) {
        if (data.requiresApproval) {
          return {
            success: true,
            requiresApproval: true,
            message: data.message || 'Registration submitted. An administrator must verify your account before you can sign in.',
          };
        }

        // Auto-login after signup
        return await login(email, password);
      }
      
      return { success: false, message: 'Signup failed' };
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          console.error('Signup timeout - server may be unavailable');
          return { success: false, message: 'Signup timed out. Please try again.' };
        } else {
          console.error('Signup error:', error.message);
          return { success: false, message: error.message };
        }
      }
      return { success: false, message: 'Signup failed' };
    }
  };

  const logout = () => {
    setUser(null);
    setAccessToken(null);
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
  };

  const updateProfile = async (updates: Partial<User>): Promise<void> => {
    if (!user || !accessToken) {
      throw new Error('No user logged in');
    }

    try {
      // Separate photo updates from other updates
      // Photos are handled separately via the photo upload endpoint
      const { photo, ...serverUpdates } = updates;
      
      // Only send server updates if there are any
      if (Object.keys(serverUpdates).length > 0) {
        const response = await fetch(`${API_BASE}/users/${user.id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`
          },
          body: JSON.stringify(serverUpdates)
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error('Server profile update error:', response.status, errorData);
          throw new Error(errorData.error || `Failed to update profile (${response.status})`);
        }
      }
      
      // Always update the local user state with all changes (including photo URL)
      const updatedUser = { ...user, ...updates };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    } catch (error) {
      console.error('Profile update error:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ user, accessToken, login, signup, logout, updateProfile, loading, refreshSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}